import { css } from 'styled-components';

import { NAV_DESKTOP } from './tokens';

/**
 * Content-area overlay positioning — keeps portalled modals/toasts anchored to
 * the MainLayout *content card* on desktop instead of the whole viewport.
 *
 * Why this is needed: the body is off-centre on desktop. `MainLayout` lays out
 * `side-menu (84px) · content · Lumio (380px)` inside a `max-width: 1920px`
 * centred row, so the content card's centre is NOT the viewport centre — an
 * `inset: 0` backdrop centres a dialog visibly to the right of the card (toward
 * the wide Lumio column), and a bottom-centre toast lands off to the side too.
 *
 * The fix can't be a static `calc()` — the offset depends on three regimes
 * (no chrome <900px, side-menu only 900–1279px, both columns ≥1280px) and the
 * `margin: 0 auto` centring. So `MainLayout` measures `.pg-body-content` live
 * (`getBoundingClientRect`, re-measured on resize) and publishes it as CSS vars
 * on `:root`:
 *   --content-left · --content-top · --content-width · --content-height
 *
 * These helpers consume those vars. The fallbacks (`0px` / `100vw` / `100dvh`)
 * reproduce the old full-viewport behaviour wherever the layout isn't mounted —
 * the configurator-v3 full-bleed wizard, the auth screens — or for the one frame
 * before the first measurement lands.
 *
 * Both engage only at `NAV_DESKTOP` (≥900px): that is exactly where the side menu
 * appears and the offset begins. Below it the chrome columns are gone and the
 * content already fills the viewport, so the existing full-screen / bottom-sheet
 * mobile treatment is left untouched.
 */

/**
 * For a full backdrop layer (`position: fixed; inset: 0`): re-pin it to the
 * content card so the dim covers only the card and the dialog centres within it.
 * Append AFTER the backdrop's own `position: fixed; inset: 0;` so the media query
 * wins. Apply to the dim layer, never the dialog box.
 */
export const contentAreaOverlay = css`
  ${NAV_DESKTOP} {
    top: var(--content-top, 0px);
    left: var(--content-left, 0px);
    right: auto;
    bottom: auto;
    width: var(--content-width, 100vw);
    height: var(--content-height, 100dvh);
    /* Round the dim to the content card's own corners (MainLayout Content: 24px). */
    border-radius: 24px;
  }
`;

/**
 * For a layer that only needs horizontal re-centring over the content card while
 * keeping its own vertical anchor (e.g. the bottom-anchored Toast): constrain the
 * horizontal span to the content card so `justify-content: center` lands on the
 * card's centre. Append AFTER the layer's own `left/right` so the media query wins.
 */
export const contentAreaCenterX = css`
  ${NAV_DESKTOP} {
    left: var(--content-left, 0px);
    right: auto;
    width: var(--content-width, 100vw);
  }
`;
